// Angular
import { Component, ElementRef, EventEmitter, HostListener, Injector, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';

// SyncFusion
import {
  AggregateService, ColumnChooserService, ColumnMenuService, ColumnModel, ContextMenuService, DetailRowService, EditService,
  ExcelExportService, FilterService, ForeignKeyService, GridComponent, GroupService, PageService, PdfExportService, ReorderService,
  ResizeService, SelectionService, SortService, ToolbarService, RowDDService, VirtualScrollService, SearchService, FreezeService, InfiniteScrollService, LazyLoadGroupService
} from '@syncfusion/ej2-angular-grids';

// Platform
//import { AuthorizeService } from 'src/external-modules/api-authorization/authorize.service';
import { CerGridSelectionService } from './cer-grid-selection.service';
import { CerGridPersistenceService } from './cer-grid-persistence.service';
import { CerAppRouteService } from 'src/cer-app/cer-app-route/cer-app-route.service';
import { CerGridFilterService } from './cer-grid-filter.service';
import { CerGridEditCellService } from './cer-grid-edit-cell.service';
import { CerGridVirtualizeService } from './cer-grid-virtualize.service';
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { CerSessionService } from '../cer-session/cer-session.service';

// Cer date
import { ViewMetadata, FieldMetadata, FieldGroupMetadata, TabMetadata, ViewMetadataUtil } from '../cer-data/cer-data-types';
import { CerDataService } from '../cer-data/cer-data.service';

// Cer form
import { CerGridCommandService, } from './cer-grid-command.service';
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum, UiKeyboardShortcut } from '@ui/cer-ui-types';
import { CerFormService, ParentDataArgs } from '../cer-form/cer-form.service';
import { CerDataMetaService } from '@data/cer-data-meta.service';
import { CerGridSetupService } from './cer-grid-setup.service';
import { CerGridEditService } from './cer-grid-edit.service';
import { CerFormPersistenceService, CerMetaUserView, ICerViewPersistenceComponent } from '../cer-form/cer-form-persistence.service';
import { CerEditTemplateService, CerEditTemplateSetup } from '../cer-edit/cer-edit-template.service';
import { CerFormDataNavigationUtil, CerFormNavigationService } from '@form/cer-form-navigation.service';
import { CerFormView, ICerViewComponent } from '@form/cer-form-view';
import { GridDesign } from './cer-grid-design';
import { CerRandom } from '../cer-util/cer-random';
import { uiCmdComponentMaximize, uiCmdNavigateItem } from '@ui/cer-ui-commands';
import { CerRouteData } from '@app/app.module-generated';
import { CerDataImportDialogService } from '@data/cer-data-excel-import/cer-data-import-dialog.service';
import { CerGridExcelService } from './cer-grid-excel.service';
import { CerDataImportClipboardService } from '@data/cer-data-excel-import/cer-data-import-clipboard.service';
import { CerGridAttachmentService } from './cer-grid-attachment.service';

export type CerGridSelectionInitial = 'default' | 'first' | 'first-no-focus' | 'none';
export type CerGridSelectionMode = 'checkboxMulti' | 'checkboxSingle' | 'multiple' | 'row' | 'cell' | 'cellBox';
export type CerGridToolbarType = 'default' | 'full' | 'simple' | 'minimal' | 'search' | 'none';
export type CerGridPaging = 'default' | 'virtual' | 'pager' | 'one-page' | 'card';
export type CerGridHeight = 'maximize' | 'auto';
export type CerToolbarTitle = 'default' | 'metadata' | 'metadata-count' | 'count' | 'disabled';
export type CerAutoFocus = 'default' | 'active' | 'disabled';
export type CerGridEditMode = 'default' | 'dialog' | 'inline' | 'inlineBatch';

export class CerGridEditSettings {
  public mode: CerGridEditMode;
  public editOnDoubleClick?: boolean;
  public editOnClick?: boolean;
  public newRowPosition?: 'Bottom';
  public editConfirm?: boolean;
  public editBatchCancelConfirm?: boolean;
  public deleteConfirm?: boolean;
  public dialog?: CerGridEditDialogSettings;
}

export class CerGridEditDialogSettings {
  public editInPane?: boolean = false;
  public width?: string;
  public height?: string;
}

/*
    <ng-container *ngIf="data.dataTypeText == 'STRING'">
        <input class="e-input" name="value" required [(ngModel)]="gridData.value" type="text" />
    </ng-container>
    <ng-container *ngIf="data.editTypedataTypeText == 'DATE'">
        <ejs-datepicker id="value" placeholder="Date" format='MM/dd/yyyy' [(ngModel)]="data.value"
            floatLabelType='Never'></ejs-datepicker>
    </ng-container>
    <ng-container *ngIf="data.dataTypeText == 'BOOLEAN'">
        <ejs-checkbox [(ngModel)]="data.value"></ejs-checkbox>
    </ng-container>

*/

@Component({
  selector: 'cer-grid',
  templateUrl: './cer-grid.component.html',
  styleUrls: ['./cer-grid.component.css'],
  providers: [
    CerGridExcelService, CerGridAttachmentService, CerDataImportDialogService, CerDataImportClipboardService,
    CerGridSetupService, CerGridCommandService, CerDataMetaService,
    CerGridVirtualizeService, CerGridSelectionService, CerGridFilterService, CerGridPersistenceService,
    CerGridEditService, CerGridEditCellService, CerEditTemplateService, CerDataService,
    PageService, EditService, ForeignKeyService, FilterService, SortService, SearchService,
    GroupService, ResizeService, ReorderService, SelectionService, ToolbarService,
    ColumnChooserService, ColumnMenuService, ContextMenuService,
    ExcelExportService, PdfExportService, DetailRowService, AggregateService,
    RowDDService, VirtualScrollService, FreezeService, InfiniteScrollService, LazyLoadGroupService]
})

export class CerGridComponent implements OnInit, OnDestroy, ICerViewPersistenceComponent, ICerViewComponent {
  @Input() id: string;
  @Input() formView: CerFormView;
  @Input() editTemplate: TemplateRef<any> = null;
  @Input() selectionInitial: CerGridSelectionInitial = 'default';
  @Input() selectionMode: CerGridSelectionMode = 'row';
  @Input() autoFocus: CerAutoFocus = 'default';
  @Input() viewMetadata: ViewMetadata;
  @Input() dataParent: boolean = false;
  @Input() dataChild: boolean = false;
  @Input() dataChildOfIds: string[];
  @Input() fieldMetadata: FieldMetadata[];
  @Input() fieldGroupMetadata: FieldGroupMetadata[];
  @Input() tabMetadata: TabMetadata[];
  @Input() toolbarCommands: UiCommand[] = [];
  @Input() toolbarType: CerGridToolbarType = 'default';
  @Input() toolbarTitle: CerToolbarTitle = 'default';
  @Input() contextMenuCommands: UiCommand[] = [];
  @Input() keyboardShortcuts: UiKeyboardShortcut[] = [];
  @Input() height: CerGridHeight = 'maximize';
  @Input() width: string | number = '100%';
  @Input() rowHeight: number = 36;
  @Input() paging: CerGridPaging = 'default';
  @Input() pageSize: number = null;
  @Input() editSettings: CerGridEditSettings;
  @Output() commmand: EventEmitter<UiCommandEvent> = new EventEmitter<UiCommandEvent>()


  private subscriptionManager$: Subscription = new Subscription();
  private formPersistenceService: CerFormPersistenceService;
  private gridPersistenceService: CerGridPersistenceService;
  constructor(
    public setupService: CerGridSetupService,
    public dataService: CerDataService,
    public formDataNavigationService: CerFormNavigationService,
    public editService: CerGridEditService,
    public editCellService: CerGridEditCellService,
    public selection: CerGridSelectionService,
    public excel: CerGridExcelService,
    public attachment: CerGridAttachmentService,
    public dataMetaService: CerDataMetaService,
    public commandService: CerGridCommandService,
    public filterService: CerGridFilterService,
    private virtualizeService: CerGridVirtualizeService,
    private formService: CerFormService,
    private editTemplateService: CerEditTemplateService,
    private ui: CerPromptService,
    public routeService: CerAppRouteService,
    private sessionService: CerSessionService,
    private injector: Injector) {
    this.formPersistenceService = this.injector.get(CerFormPersistenceService, null, { optional: true }); // If constructed outside CerFormComponent
    this.gridPersistenceService = this.injector.get(CerGridPersistenceService, null, { optional: true }); // If constructed outside CerFormComponent
  }

  ngOnInit(): void {
    this.subscriptionManager$.add(this.formService.formCommand$.subscribe(event => this.onFormCommand(event)));
    this.subscriptionManager$.add(this.editTemplateService.templateSetup$.subscribe(setup => this.templateSetup = setup));
    this.subscriptionManager$.add(this.commandService.command$.subscribe(cmd => this.onCommand(cmd)));
  }


  ngOnDestroy(): void {
    this.commandService.cmdDispatchActionBegin(UiActionTypeEnum.Destroyed);
    this.subscriptionManager$.unsubscribe();
    if (this.grid) {
      var grid: GridComponent = this.grid;
      this.grid = null;
      grid.destroy();
    }
  }

  public onViewCommand(event: UiCommandEvent) {
    switch (event.actionType) {
      case 'dataLoad':
        if (event.rowsData != null) {
          this.onDataLoad(event.rowsData);
        }
        break;
      case 'reflow':
        this.virtualizeService.doReflow();
        break;
      case 'research':
        this.research();
    }
  }

  //#region DataChild=true: Parent data subscribe (When child role, and parent has new row selected)
  private parentDataChildHasSetup(): boolean {
    return (this.formService != null && this.dataChild && ViewMetadataUtil.linkIsParentLinkDefined(this.dataService.viewMetadata));
  }

  private onParentDataInitial(): boolean {
    if (this.formService != null && this.dataChild && this.dataChildOfIds?.length > 0) {
      const lastestParentData = this.formService.parentDataLatestTopicArray[this.dataChildOfIds[0]];
      if (lastestParentData != null) {
        this.onParentData(lastestParentData);
        return true;
      }
    }
    return false;
  }

  private parentData: any = null;
  public parentDataGet(): any {
    return this.parentData;
  }

  public onParentData(parentDataArgs: ParentDataArgs) {
    if (parentDataArgs && this.dataChild) {
      if (((this.dataChildOfIds == null || this.dataChildOfIds.length == 0) && this.id != parentDataArgs.id) || 
           (this.dataChildOfIds?.includes(parentDataArgs.id))) {
        this.parentData = parentDataArgs.row;
        this.onParentDataHandle(parentDataArgs);
      }
    }
  }

  private onParentDataHandle(parentDataArgs: ParentDataArgs) {
    const v = this.dataService.viewMetadata;
    if (v == null) {
      return;
    }
    var parentKeyValue = ViewMetadataUtil.parentValueGet(v, parentDataArgs);
    if (parentKeyValue) {
      //if (parentDataArgs.row || parentDataArgs.stable) {
      if (this.dataService.linkParentValueRefresh(parentKeyValue)) {
        if (this.grid) {
          if (this.commandService.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, parentDataArgs, null, UiActionTypeEnum.RefreshFromParent, null, this.parentData) == false) {
            this.commandService.dataSourceInit();
            if (this.commandService.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, parentDataArgs, null, UiActionTypeEnum.RefreshFromParent, null, this.parentData) == false) {
              //this.commandService.refreshColumns();
            };
          }
        }
      }
    }
    else if (ViewMetadataUtil.linkIsParentLinkDefined(v)) {
      this.commandService.dataSourceInitLocalEmpty();
    }
    this.selection.nextSelectionIsExtenal();
  }

  private onAddRowInitFromParentData(event: UiCommandEvent) {
    ViewMetadataUtil.fieldValueInitFromParent(this.viewMetadata, event.args.data, this.parentData);
  }
  //#endregion

  //#region DataParent=true: Parent data publish to children
  // When parent row selected/deselected
  public parentDataNext(data: any, stable: boolean = false) {
    if (this.dataParent) {
      const name = this.viewMetadata?.entityName ?? this.viewMetadata?.dataApiName ?? this.viewMetadata?.name;
      var parentData: ParentDataArgs = { typeName: name , row: data, id: this.id, topic: this.id, stable: stable };
      this.formService.parentDataNext(parentData);
      //this.grid.element.focus(); (try to send focus back to parent grid, but this is not the right hook)
    }
  }

  private parentDataSelectingStarted: boolean = false;
  private parentDataResetOnRefreshComplete: boolean = false;
  private onCommandParentDataPublish(event: UiCommandEvent) {
    if (!this.dataParent) {
      return; // Only publish parent data is "dataParent == true"
    }
    switch (event.source) {
      // ActionBegin: Refresh
      case UiCommandSourceEnum.ActionBegin:
        switch (event.actionType) {
          case UiActionTypeEnum.Refresh:
            this.parentDataResetOnRefreshComplete = true;  // Delay refresh parent data on coming selections
            break;
        }
        break;
      // ActionComplete: Selection+Refresh
      case UiCommandSourceEnum.ActionComplete:
        switch (event.actionType) {
          case UiActionTypeEnum.CellSelecting:
          case UiActionTypeEnum.RowSelecting:
            this.parentDataSelectingStarted = true; // We have a new selection - don't reset on coming RowDeselect
            break;
          case UiActionTypeEnum.CellSelect:
          case UiActionTypeEnum.RowSelect:
            if (!this.parentDataResetOnRefreshComplete) {
              this.parentDataNext(event.args.data, true);
            }
            this.parentDataSelectingStarted = false;
            break;
          case UiActionTypeEnum.CellDeselect:
          case UiActionTypeEnum.RowDeselect:
            if (!this.parentDataResetOnRefreshComplete && !this.parentDataSelectingStarted) {
              this.parentDataNext(null, false);
            }
            break;
          case UiActionTypeEnum.Refresh:
            if (this.parentDataResetOnRefreshComplete) {
              this.parentDataResetOnRefreshComplete = false;
              this.parentDataNext(this.selection.rowSelectedData(), true);
            }
            break;
          case UiActionTypeEnum.DataBound:
            if (!event.rowsData || event.rowsData.length == 0) {
              this.parentDataNext(null, true);
            }

        }
    }
  }
  //#endregion

  //#region View port
  protected isMaximized: boolean = false;
  public setIsMaximized(isMaximized: boolean) {
    if (this.isMaximized != isMaximized) {
      this.isMaximized = isMaximized;
      var this_ = this;
      setTimeout(() => this_.setGridSize());
    }
  }

  public isEdit(): boolean {
    return this.grid?.isEdit;
  }
  public reflow() {
    this.setGridSize();
  }

  private setGridSize() {
    if (this.containerDiv) {
      var rect: DOMRect = this.containerDiv.nativeElement.getBoundingClientRect();
      if (rect?.height) {
        this.commandService.reflow();
      }
    }
  }
  //#endregion

  //#region General event handling
  public onFormCommand(event: UiCommandEvent) {
    if (event.source == 'designChange') {
      this.commandService.reflow();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    this.commandService.gridOnKeyDown(e);
  }

  public onCommand(event: UiCommandEvent) {
    event.appComponent = this;

    if (event.source == UiCommandSourceEnum.ActionComplete) {
      switch (event.actionType) {
        case UiActionTypeEnum.DataBound:
          this.onDataBound();
          break;
        case UiActionTypeEnum.DataLoad:
          this.onDataLoad(event.args);
          break;
        case UiActionTypeEnum.ActionFailure:
          var args = event.args as any;
          while (args?.error && !args?.status) {
            args = args.error;
          }
          this.ui.errorApi(args);
          break;
      }
    }

    switch (event.commandId) {
      case uiCmdNavigateItem.id:
        this.commandService.navigationEvent(event);
        break;
      case uiCmdComponentMaximize.id:
        this.setIsMaximized(this.isMaximized ? false : true);
        break;
    }
    if (event.commandId?.length > 0) {
      if (this.formService.isFormPaneCommand(event.commandId)) {
        this.setIsMaximized(false);
      }
    }

    this.formService.childCommand$.next(event);
    if (event.cancel !== true) {
      if (event.source == UiCommandSourceEnum.ActionBegin && (event.actionType == UiActionTypeEnum.Add)) {
        if (this.dataChild) {
          // onAddRowInitFromParent
          this.onAddRowInitFromParentData(event);
        }
      }
      if (this.dataParent) {
        this.onCommandParentDataPublish(event);
      }
    }
    if (event.cancel !== true) {
      this.commmand.emit(event);
    }
  }

  private onDataBound() {
    this.isGridDataBound = true;
    this.routeService.setIsLoading(false);

  }

  public onDataLoad(data: Object[]) {
    if (data && this.grid) {
      this.grid.dataSource = data;
      /*this.viewMetadata.data = data;
      this.dataService.dataManagerSetData(this.grid.dataSource, data);*/
      if (this.isGridDataBound) {
        this.grid.refresh();
      }
      this.isGridDataBound = true;
    }
  }
  //#endregion

  //#region Access methods for edit + selection services
  public isEditable(): boolean {
    return this.editService.canEdit();
  }

  public edit(): CerGridEditService {
    return this.editService;
  }

  public parentRowData(): any {
    return this.parentData;
  }

  public rowSelectByIdx(idx: number) {
    this.selection.rowSelectByIdx(idx, false);
  }

  public rowSelectedData(): any {
    return this.selection.rowSelectedData();
  }

  public rowsSelectedData(): any[] {
    return this.selection.rowsSelectedData();
  }

  public rowsSelectedId(): number[] {
    return this.selection.rowsSelectedId();
  }

  public rowSelectedKeep() {
    this.selection.rowSelectedSave();
  }

  public rowSelectedRefresh(): void {
    this.selection.rowSelectRefresh();
  }

  public rowSelectedActive(): boolean {
    return this.selection.rowSelectedActive();
  }

  public rowSelectVirtual(idx: number, rowData: any) {
    const args = { data: rowData, rowIdx: idx};
    this.commandService.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.RowSelect, [args.data], args.data);
  }
    
  public disable() {
    this.commandService.disable();
  }

  public enable() {
    this.commandService.enable();
  }

  public research() {
    this.commandService.refresh();
  }

  public rowDataSetById(id: any, rowData: any) {
    this.selection.rowDataSetById(id, rowData);
  }
  //#endregion

  //#region Init component
  public isGridInit: boolean = false;
  public isGridDataBound: boolean = false;
  public design: GridDesign;
  public setupComplete: boolean = false;
  public templateSetup: CerEditTemplateSetup = null;

  // Container div
  public containerDiv: ElementRef;
  @ViewChild('containerDiv') set containerDivContent(content: ElementRef) {
    if (content && content != this.containerDiv) {
      this.containerDiv = content;
      this.editService.dialogEditInit(content);
    }
  }

  @ViewChild('chatTemplate', { static: false }) set chatTemplateContent(content: NgModel) {
    if (content && content != this.editCellService.chatTemplate) {
      this.editCellService.chatTemplate = content;
      var chatColumn: ColumnModel = this.editCellService.chatColumn;
      if (chatColumn) {
        (chatColumn.template as any) = this.editCellService.chatTemplate;
      }
    }
  }

  // Grid reference
  public grid: GridComponent = null;
  @ViewChild(GridComponent, { static: false }) set setViewChildGrid(grid: GridComponent) {
    if (grid && (!this.grid || !this.isGridInit)) {
      this.grid = grid;
      this.init();
    }
  }

  public init() {
    if (!this.isGridInit) {
      if (this.grid && this.viewMetadata) {
        if ((this.dataService /*&& this.dataService.authToken*/) || (this.viewMetadata?.offline == true && this.viewMetadata?.data)) {
          this.isGridInit = true;
          (<any>this.grid).cerGrid = this;
          (<any>this.grid).viewName = this.viewMetadata?.name ?? this.viewMetadata.dataApiName;
          this.initFromFormView();
          this.triggerActionBeginInit();
          this.dataSetup();
          if (!(this.id?.length > 0)) {
            this.id = this.getId();
          }
          var userUiShowPager: boolean = this.sessionService.user.dto()?.uiPagerShow == true;
          this.gridDesignSetup(userUiShowPager);
          this.gridSetup()
          if (this.gridPersistenceService && this.formPersistenceService) {
            this.gridPersistenceService.init(this, this.design);
            this.formPersistenceService.registerComponent(this);
          }
          this.dataService?.setupPost();
          this.setupComplete = true;
          if (this.gridPersistenceService && this.formPersistenceService) {
            this.gridPersistenceService.saveDefault();
          }
        }
      }
    }
  }

  private triggerActionBeginInit() {
    this.commmand.emit({
      appComponent: this,
      formView: this.formView,
      viewMetadata: this.viewMetadata,
      source: UiCommandSourceEnum.ActionBegin,
      actionType: UiActionTypeEnum.Init,
      subAction: null, args: null, commandId: null
    });
  }

  private initFromFormView() {
    if (this.formView?.viewType == 'custom') {
      // custom component
      if (this.formView.parentView?.dataParent && this.formView.dataChild) {
        this.formViewCustomSetDataChild();
        this.formViewCustomSubscribeParentData();
      }
    }
  }

  private formViewCustomSubscribeParentData() {
    const fs = this.formService;
    if (fs) {
      const v = this.formView;
      const fsParent = v.routeOptions?.context?.formService;
      if (fsParent && fsParent != fs) {
        const parentDataInitial = fsParent.parentDataLatestTopicArray;
        if (parentDataInitial) {
          fs.parentDataLatestTopicArray = parentDataInitial;
          this.onParentDataInitial();
        }
        this.subscriptionManager$.add(fsParent.parentData$.subscribe(
          dataArgs => this.onParentData(dataArgs))
        );
      }
    }
  }

  private formViewCustomSetDataChild() {
    const fv = this.formView;
    if (fv) {
      this.dataChild = fv.dataChild;
      this.dataChildOfIds = fv.dataChildOfIds;
      if (this.design) {
        this.design.dataChildrenExists = fv.dataChildrenExists
      }
      if (fv.routeOptions?.navigation != null) {
        const nav = fv.routeOptions.navigation;
        if (nav) {
          CerFormDataNavigationUtil.initViewMetadata(nav, this.viewMetadata);
        }
        if (nav?.filterSingleRow || ViewMetadataUtil.linkIsUniqueRow(this.viewMetadata, (<CerRouteData>fv.routeOptions?.routeData)?.entities)) {
          ViewMetadataUtil.initSingleRow(this.viewMetadata);
          this.initSingleRow();
        }
      }
    }
  }

  private initSingleRow() {
    if (this.paging == 'default') {
      this.paging = 'card';
      if (this.toolbarTitle == 'default') {
        this.toolbarTitle = 'metadata';
      }
    }
  }
  public rowSelectEnsure() {
    if (!this.selection.rowSelectedActive()) {
      this.selection.rowSelectFirst();
    }
  }

  private getId(): string {
    var id: string[] = ['grid'];
    var vm = this.viewMetadata;
    if (vm != null) {
      id.push(vm.dataApiName ?? vm.dataApiName ?? vm.text);
    }
    id.push(CerRandom.AsString(10))
    return id.join('-');
  }

  private dataSetup() {
    this.fieldMetadata = this.dataMetaService.init(this.viewMetadata, this.fieldMetadata, this.fieldGroupMetadata, this.tabMetadata);
    this.setupService.dataService = this.dataService; // TODO: cleanup ref
  }

  private gridDesignSetup(userSettingPagerShow: boolean) {
    // Basic setup of grid
    var isChildView = this.dataChild == true;
    var paging: CerGridPaging = this.paging;
    if (paging == 'default' || paging == null) {
      if (isChildView) {
        paging = 'one-page';
      }
      else if (userSettingPagerShow) {
        paging = 'pager';
      }
      else {
        paging = 'virtual';
      }
    }

    var d = new GridDesign();
    d.id = this.id;
    d.toolbarType = isChildView && this.toolbarType == 'default' ? 'minimal' : this.toolbarType;
    d.pagingVirtual = (paging == 'virtual' || paging == 'one-page');
    d.card = (paging == 'card');
    d.allowPaging = (paging == 'pager');
    d.fixedHugePage = (paging == 'one-page');
    d.groupingLazyLoading = d.pagingVirtual;
    d.useEditTemplate = (this.editTemplate != null || this.tabMetadata?.length > 0);
    d.useMaxHeight = (['maximize'].includes(this.height));
    d.autoFocus = (this.autoFocus != 'disabled');;
    d.toolbarTitle = this.toolbarTitle == 'default' ? (d.allowPaging ? 'metadata' : 'metadata-count') : this.toolbarTitle;
    d.dataParent = this.dataParent;
    d.dataChild = this.dataChild;
    d.dataChildrenExists = this.formView != null ? this.formView.dataChildrenExists === true : true; // Assume true
    d.selectionInitial = this.selectionInitial == 'default' ? (isChildView ? 'first-no-focus' : 'first') : this.selectionInitial;
    d.selectionMode = this.selectionMode;
    d.editSettings = this.editSettings;
    this.design = d;
  }

  private gridSetup() {
    this.editCellService.init(this.grid);
    this.filterService.init(this.grid, this.design);
    this.setupService.init(this.grid, this.design);
    this.editCellService.initPost();
    this.virtualizeService.init(this.grid, this.design);
    this.grid.width = this.width;
    this.grid.rowHeight = this.design.card ? null : this.rowHeight;

    let onLine = true;
    if (this.dataChild && this.parentDataChildHasSetup()) {
      onLine = this.onParentDataInitial();
      this.subscriptionManager$.add(this.formService.parentData$.subscribe(data => this.onParentData(data)));
    }
    if (onLine && this.dataService.dataSourceType == 'datamanager' && this.dataService.dataManager) {
      this.grid.dataSource = this.dataService.dataManager;
      this.grid.query = this.dataService.query;
    }

    this.editService.init(this, this.grid, this.design);
    this.selection.init(this, this.grid, this.design);
    this.excel.init(this);
    this.attachment.init(this);
    this.commandService.init(this, this.design, this.toolbarCommands, this.contextMenuCommands, this.keyboardShortcuts);
  }
  //#endregion

  public isRendered(): boolean {
    const ds = this.grid?.renderModule?.data?.dataManager?.dataSource;
    return (ds?.offline == false) || (this.viewMetadata.dataOnParentDataOnly === true);
  }

  //#region Persistence
  public getPersistenceId(): string {
    return this.gridPersistenceService?.getId();
  }

  public getPersistence(): any {
    return this.gridPersistenceService?.get();
  }

  public applyPersistence(view: CerMetaUserView, persistenceObject: any): boolean {
    return this.gridPersistenceService?.apply(view, persistenceObject);
  }

  public applyPersistenceComplete(view: CerMetaUserView): void {
    this.gridPersistenceService?.applyComplete(view);
  }
  //#endregion Persistence

  //#region Manual setting of data
  /*
  public dataNext(data: Object[], force: boolean = false) {
    if (data) {
      var isChanged: boolean = false;
      if (this.grid) {
        if (this.grid.dataSource instanceof DataManager) {
          var dm: DataManager = this.grid.dataSource as DataManager;
          if (dm && !CerGridSelectionService.rowsDataEqual(dm.dataSource.json, data)) {
            dm.dataSource.json = data;
            isChanged = true;
          }
        }
        else {
          if (force || !CerGridSelectionService.rowsDataEqual(this.grid.dataSource as any[], data)) {
            this.grid.dataSource = new DataManager(data);
            isChanged = true;
          }
        }
      }
      if (this.dataService && isChanged) {
        this.dataService.data$.next(data);
        this.grid?.refresh();
      }
    }
  }
  */
  //#endregion

}
